'use client';

import { FC } from 'react';
import Image from 'next/image';
import FadeIn from '@/components/FadeIn/FadeIn';
import {
  ProductHero,
  TrustBar,
  ProductFAQ,
  ProductCTAFooter,
} from '../components';

// Feature card component
const FeatureCard: FC<{
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  compactImage?: boolean; // Reduce bottom padding for cards with extra whitespace in image
}> = ({ title, description, image, imageAlt, compactImage }) => (
  <div
    className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 lg:p-10"
    style={compactImage ? { paddingBottom: '24px' } : undefined}
  >
    <div className="mb-6">
      <h3
        className="font-medium text-black mb-3"
        style={{ fontSize: 'clamp(1.25rem, 2vw, 2rem)' }}
      >
        {title}
      </h3>
      <p
        className="text-slate-600"
        style={{ fontSize: 'clamp(1rem, 1.25vw, 1.25rem)' }}
      >
        {description}
      </p>
    </div>
    <div className="rounded-xl overflow-hidden bg-white shadow-sm">
      <Image
        src={image}
        alt={imageAlt}
        width={1200}
        height={600}
        className="w-full h-auto object-contain"
      />
    </div>
  </div>
);

const AI_SEO_FEATURES = {
  compareChannel: {
    title: 'Compare different channel performances quickly',
    description: 'Identify which platforms deliver the best results and reallocate budgets in seconds.',
    image: '/images/products/ai-seo/compare-channel.svg',
    imageAlt: 'Channel performance dashboard',
  },
  discoverModels: {
    title: 'Discover which AI models rank your brand',
    description: 'See how ChatGPT, Gemini & other AI engines rank your brand.',
    image: '/images/products/ai-seo/discover-ai-models.svg',
    imageAlt: 'AI model rankings interface',
  },
  trackPerformance: {
    title: "Track your brand's performance metrics",
    description: 'Measure visibility, mentions and citation to understand how AI platforms recognize your brand.',
    image: '/images/products/ai-seo/track-performance.svg',
    imageAlt: 'Brand performance metrics dashboard',
  },
  findFixGaps: {
    title: 'Find and fix AI visibility gaps',
    description: 'Identify missing citations or backlinks — and take precise steps to improve your brand authority.',
    image: '/images/products/ai-seo/find-fix-gaps.svg',
    imageAlt: 'AI visibility gaps analysis',
  },
};

const AI_SEO_FAQS = [
  {
    question: 'How long does it take to set up AI SEO tracking?',
    answer: 'AI SEO tracking can be set up in minutes. Simply connect your brand and we\'ll start monitoring AI mentions across major platforms.',
  },
  {
    question: 'Which AI platforms do you track?',
    answer: 'We track your brand across ChatGPT, Gemini, Claude, Perplexity and other major AI platforms to give you comprehensive visibility insights.',
  },
  {
    question: 'Can I customize reports and dashboards?',
    answer: 'Yes, you can customize reports and dashboards with your brand colors and export them for stakeholder presentations.',
  },
  {
    question: 'Which AI models does the platform monitor?',
    answer: 'We monitor all major AI platforms including OpenAI\'s GPT models, Google\'s Gemini, Anthropic\'s Claude, and emerging AI search engines.',
  },
];

const AISEOPage: FC = () => {
  return (
    <div className="bg-white">
      <ProductHero
        iconSrc="/images/products/icon-ai-seo.svg"
        label="AI SEO"
        title="Identity how AI talks about your brand"
        highlightedWord="talks"
        subtitle="Measure visibility, mentions, and performance across channels — then take data-backed actions to improve your authority and ranking in AI-driven search."
        primaryCTA={{
          label: 'Book a demo',
          href: '/book-demo',
        }}
      />

      <TrustBar />

      {/* Custom Feature Section with specific layout */}
      <section className="py-16 md:py-24">
        <div className="container-fluid px-4 md:px-7.5 mx-auto" style={{ maxWidth: 'clamp(1366px, 85vw, 1800px)' }}>
          {/* Section Title */}
          <FadeIn variant="up" delay={0.1}>
            <div className="text-center mb-12">
              <h2
                className="font-medium text-black"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 3rem)' }}
              >
                All your <span style={{ color: '#094FFE' }}>AI-SEO</span> insights in one place
              </h2>
            </div>
          </FadeIn>

          {/* Features with custom layout */}
          <div className="space-y-6 md:space-y-8">
            {/* Row 1: Full width - Compare channel */}
            <FadeIn variant="up" delay={0.15}>
              <FeatureCard {...AI_SEO_FEATURES.compareChannel} />
            </FadeIn>

            {/* Row 2: Two columns - Discover models + Track performance */}
            <FadeIn variant="up" delay={0.2}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-start">
                <FeatureCard {...AI_SEO_FEATURES.discoverModels} compactImage />
                <FeatureCard {...AI_SEO_FEATURES.trackPerformance} />
              </div>
            </FadeIn>

            {/* Row 3: Full width - Find and fix gaps */}
            <FadeIn variant="up" delay={0.25}>
              <FeatureCard {...AI_SEO_FEATURES.findFixGaps} />
            </FadeIn>
          </div>
        </div>
      </section>

      <ProductFAQ faqs={AI_SEO_FAQS} />

      <ProductCTAFooter
        productName="AI SEO"
        title="Smarter sales. Faster results."
        subtitle="Powered by marketbetter"
      />
    </div>
  );
};

export default AISEOPage;
